// dependencies
import React, { useEffect, useState } from 'react';
import { useFormControl, ReactFormInputOptions } from '@vendure/admin-ui/react';

const DEFAULT_TIME = '23:59:59';

const DatetimeInput = ({ readonly, config, formControl }: ReactFormInputOptions) => {
  const { value, setFormValue } = useFormControl();

  const minDate = (config as any)?.min ? toDateInputValue((config as any).min) : undefined;
  const maxDate = (config as any)?.max ? toDateInputValue((config as any).max) : undefined;

  const [ctrlDisabled, setCtrlDisabled] = useState<boolean>(() => !!formControl?.disabled);

  useEffect(() => {
    if (!formControl) return;
    setCtrlDisabled(!!formControl.disabled);
    const sub = (formControl as any).statusChanges?.subscribe(() => {
      setCtrlDisabled(!!formControl.disabled);
    });
    return () => sub?.unsubscribe?.();
  }, [formControl]);

  const disabled = readonly || ctrlDisabled;

  const dateValue = value ? toDateInputValue(value) : '';

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const datePart = event.target.value;
    if (!datePart) {
      setFormValue(null);
      return;
    }
    const date = new Date(`${datePart}T${DEFAULT_TIME}`);
    if (isNaN(date.getTime())) {
      setFormValue(null);
      return;
    }
    setFormValue(date.toISOString());
  };

  const clearValue = () => setFormValue(null);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <input
        type='date'
        value={dateValue}
        min={minDate}
        max={maxDate}
        disabled={disabled}
        readOnly={disabled}
        onChange={handleDateChange}
        style={{
          padding: '4px 6px',
          opacity: disabled ? 0.55 : 1,
          cursor: disabled ? 'not-allowed' : 'auto',
          pointerEvents: disabled ? 'none' : 'auto',
          backgroundColor: disabled ? 'var(--color-component-bg-200, #f3f4f6)' : undefined,
          color: disabled ? 'var(--color-weight-500, #9ca3af)' : undefined
        }}
      />
      {!disabled && value ? (
        <button
          type='button'
          className='button secondary'
          onClick={clearValue}
          title='Clear'
        >
          ✕
        </button>
      ) : null}
    </div>
  );
};

function toDateInputValue(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) {
    return '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export default DatetimeInput;
